import { useEffect, useRef, useState } from 'react';
import { Modal } from '@safetyhub/ui';
import { Combobox } from '../common/Combobox';
import { EditModalFooter } from '../common/EditModalFooter';
import { normalizePhoneInput, PHONE_MAX_DIGITS } from '../../lib/phone';

export interface SiteContactFormValue {
  name: string;
  phone: string;
  department?: string;
  position?: string;
  email?: string;
}

const EMPTY_FORM: SiteContactFormValue = {
  name: '',
  phone: '',
  department: '',
  position: '',
  email: '',
};

/**
 * dirty 판정 스냅샷 — 편집 입력 전수. 제출이 값을 trim해서 보내므로 비교도 trim 기준이다
 * (공백만 다른 값은 저장해도 서버 값이 그대로라 "변경"으로 치지 않는다).
 */
function contactFormSnapshot(v: SiteContactFormValue): string {
  return JSON.stringify({
    name: v.name.trim(),
    phone: v.phone.trim(),
    department: v.department?.trim() ?? '',
    position: v.position?.trim() ?? '',
    email: v.email?.trim() ?? '',
  });
}

/**
 * 수정 모드 푸터 — 취소가 base Modal의 requestClose로 수렴해야 dirty 닫기 확인을 탄다.
 * (requestClose context는 Modal children **하위 컴포넌트**에서만 잡히므로 분리했다 — base Modal 규약)
 */
function ContactFormFooter({
  isEditMode,
  isDirty,
  isSubmitting,
  canSubmit,
  submitLabel,
  onSave,
  onFallbackClose,
}: {
  isEditMode: boolean;
  isDirty: boolean;
  isSubmitting: boolean;
  canSubmit: boolean;
  submitLabel: string;
  onSave: () => void;
  onFallbackClose: () => void;
}) {
  return (
    <EditModalFooter
      isDirty={isDirty}
      showHint={isEditMode}
      submitLabel={submitLabel}
      submitDisabled={!canSubmit || (isEditMode && !isDirty)}
      busy={isSubmitting}
      onSubmit={onSave}
      onFallbackClose={onFallbackClose}
    />
  );
}

export interface SiteContactFormModalProps {
  /** 모달 표시 여부 */
  open: boolean;
  /** 모달 제목 (기본: "새 담당자 추가" 또는 "담당자 수정") */
  title?: string;
  /** 초기값 (수정 모드용, 없으면 빈 폼) */
  initialValue?: SiteContactFormValue;
  /** 부서 자동완성 제안 목록 */
  departmentSuggestions?: string[];
  /** 연락처 필수 여부 (기본: true — 현장 담당자 등록), false면 이름만 필수 */
  requirePhone?: boolean;
  /** 이메일 필드 표시 (기본: true) */
  showEmail?: boolean;
  /** 부서/직책 필드 숨김 (인적 정보만 수정할 때) */
  hideRole?: boolean;
  /** z-index 클래스 (기본: z-[70], sub modal 용도는 z-[80]) */
  zIndexClass?: string;
  /** 제출 — Promise resolve 시 모달 닫힘 */
  onSubmit: (value: SiteContactFormValue) => Promise<void>;
  /** 취소/배경 클릭 */
  onCancel: () => void;
}

/**
 * SiteContact 등록/수정 공통 모달
 *
 * 사용처:
 * - 현장 상세 페이지 (SiteContactsSection)
 * - SMS 보고서 (RiskAssessmentStep) — 새 담당자 추가
 * - 일일점검일지 (ConfirmerPicker) — 새 담당자 추가
 *
 * 부서는 Combobox 자동완성 + 새 부서 자유 입력. 입력된 부서는
 * createSiteContact API 통해 SiteContact.department로 저장되어
 * 다음 조회 시 자동으로 메타에 누적됨.
 *
 * z-index: 모달 정책에 따라 root z-[70]. Layout 헤더 z-[60]/고정 배너(`BANNER_LAYER`) 위.
 * sub modal(MemberPhoneConflictModal 등 z-[80])이 이 모달 위에 표시 가능.
 *
 * 오버레이·Esc·오버레이 클릭 닫기·포커스 트랩·스크롤 잠금은 공용 [[Modal]]이 소유한다.
 * 제출 중에는 닫기 수단을 전부 막는다(오버레이·Esc·✕) — 이관 전 handleClose의 early-return과 동일.
 */
export function SiteContactFormModal({
  open,
  title,
  initialValue,
  departmentSuggestions = [],
  requirePhone = true,
  showEmail = true,
  hideRole = false,
  zIndexClass = 'z-[70]',
  onSubmit,
  onCancel,
}: SiteContactFormModalProps) {
  const [value, setValue] = useState<SiteContactFormValue>(initialValue ?? EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // open될 때 초기값 동기화
  useEffect(() => {
    if (open) {
      setValue(initialValue ?? EMPTY_FORM);
      setError(null);
    }
  }, [open, initialValue]);

  const canSubmit =
    !!value.name.trim() && (!requirePhone || !!value.phone.trim()) && !isSubmitting;

  // 수정 모드(initialValue 존재)에서만 무변경 저장을 막는다 — 생성 경로는 기존 동작 유지.
  const isEditMode = !!initialValue;
  const isDirty =
    isEditMode &&
    contactFormSnapshot(value) !== contactFormSnapshot(initialValue ?? EMPTY_FORM);

  async function handleSubmit() {
    // 버튼 disabled를 우회하는 경로(성명 입력 Enter)까지 막는다.
    if (!canSubmit || (isEditMode && !isDirty)) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        name: value.name.trim(),
        phone: value.phone.trim(),
        department: value.department?.trim() || undefined,
        position: value.position?.trim() || undefined,
        email: showEmail ? value.email?.trim() || undefined : undefined,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    if (isSubmitting) return;
    setValue(EMPTY_FORM);
    setError(null);
    onCancel();
  }

  const inputClass =
    'w-full px-3 py-2 border border-line rounded-control text-sm focus:ring-1 focus:ring-brand-400 focus:outline-none disabled:bg-surface-soft disabled:text-ink-500';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title ?? (initialValue ? '담당자 수정' : '새 담당자 추가')}
      size="md"
      overlayZClassName={zIndexClass}
      closeOnOverlay={!isSubmitting}
      closeOnEsc={!isSubmitting}
      showCloseButton={!isSubmitting}
      initialFocusRef={nameInputRef}
      isDirty={isDirty}
      footer={
        <ContactFormFooter
          isEditMode={isEditMode}
          isDirty={isDirty}
          isSubmitting={isSubmitting}
          canSubmit={canSubmit}
          submitLabel={isEditMode ? '수정' : '추가'}
          onSave={handleSubmit}
          onFallbackClose={handleClose}
        />
      }
    >
      {error && (
        <div className="mb-3 rounded-card bg-red-50 p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      <div className="space-y-3">
        {!hideRole && (
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">담당부서</label>
            <Combobox
              value={value.department || ''}
              onChange={(v) => setValue({ ...value, department: v })}
              suggestions={departmentSuggestions}
              placeholder="예: 안전관리팀"
              disabled={isSubmitting}
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">
            성명 <span className="text-red-500">*</span>
          </label>
          {/* Enter=제출은 이 입력에만 건다(폼 모달이라 base는 Enter를 강제하지 않는다).
              Esc는 base가 document 레벨에서 처리하므로 여기서 다시 다루지 않는다. */}
          <input
            ref={nameInputRef}
            type="text"
            value={value.name}
            onChange={(e) => setValue({ ...value, name: e.target.value })}
            placeholder="이름"
            disabled={isSubmitting}
            className={inputClass}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
        </div>
        {!hideRole && (
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">직책</label>
            <input
              type="text"
              value={value.position ?? ''}
              onChange={(e) => setValue({ ...value, position: e.target.value })}
              placeholder="예: 현장소장"
              disabled={isSubmitting}
              className={inputClass}
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-ink-700 mb-1">
            연락처 {requirePhone && <span className="text-red-500">*</span>}
          </label>
          {/* 숫자만 — 마법사 쪽(ContactFormFields)과 같은 규칙(lib/phone). 두 입력이 다르게
              굴면 같은 화면 안에서 되는 값과 안 되는 값이 갈린다. */}
          <input
            type="tel"
            inputMode="numeric"
            maxLength={PHONE_MAX_DIGITS}
            value={value.phone}
            onChange={(e) => setValue({ ...value, phone: normalizePhoneInput(e.target.value) })}
            placeholder="01012345678"
            disabled={isSubmitting}
            className={inputClass}
          />
        </div>
        {showEmail && (
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">이메일</label>
            <input
              type="email"
              value={value.email ?? ''}
              onChange={(e) => setValue({ ...value, email: e.target.value })}
              placeholder="email@example.com"
              disabled={isSubmitting}
              className={inputClass}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
